import axios from "axios";
import React, {
  useState,
  useEffect,
  useRef,
  useReducer,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { farmageo_api } from "../../../config";

import OrixiaSpinner from "../components/OrixiaSpinner";

import ListadoContext from "./context/ListadoContext";
import { ListadoStateProvider } from "./context/ListadoStateContext";
import { createListadoStateStore } from "./context/ListadoStateStore";
import PantallaContext from "../context/PantallaContext";

import FuncionesContext from "../context/FuncionesContext";
import ListadoReducer, { initialState } from "./context/ListadoReducer";
import { useRefDataStore, useRefStore } from "../../..";
import { addData, addsql } from "../context/PantallaReducer";
import { useLocation } from "react-router-dom";
import {
  agregarModoFiltrosAlBody,
  crearQueryFiltrosParaRequest,
  prepararFiltrosParaRefrescar,
} from "../helper/filtrosAlRefrescar";
import {
  esValorTodasSelect,
  obtenerValorFiltroConfigurado,
  restaurarValoresTodasEnFiltros,
} from "../helper/opcionesFiltros";

const S = require("sweetalert2");

const objetoQueryFiltrosdeF = (
  filtros,
  filtrosAAplicar,
  pantalla,
  quienLlama
) => {
  let queryfiltros = Object.assign(
    {
      pantalla: pantalla,
    },
    filtrosAAplicar
  );

  filtros.forEach((f) => {
    queryfiltros[f.id_a] = f.default;
    // eslint-disable-next-line no-unused-expressions
    f.sc_hijos?.forEach((fh) => {
      queryfiltros[fh.id_a] = fh.default;
    });
  });

  if (!filtrosAAplicar) return queryfiltros;

  const completarQueryFiltros = (queryfiltros, filtrosAAplicar, f) => {
    const filtroConfigurado = obtenerValorFiltroConfigurado({
      filtros: filtrosAAplicar,
      id_a: f.id_a,
      clave_envio: f.clave_envio,
    });

    if (filtroConfigurado.encontrado) {
      if (typeof filtroConfigurado.valor === "number") {
        queryfiltros[f.id_a] = filtroConfigurado.valor;
      }

      if (typeof filtroConfigurado.valor === "string") {
        queryfiltros[f.id_a] = filtroConfigurado.valor.toString().trim();
      }

      if (Array.isArray(filtroConfigurado.valor)) {
        queryfiltros[f.id_a] = filtroConfigurado.valor.toString().trim();
      }
    }

    if (f.componente === "hidden") {
      queryfiltros[f.id_a] =
        filtrosAAplicar[f.id_a] ?? f.default ?? "filtro_hidden_sin_valor";
    }

    if (
      !queryfiltros[f.id_a] ||
      queryfiltros[f.id_a] === null ||
      queryfiltros[f.id_a]?.toString()?.trim() === "" || // esto sobreescribe los valores de string vacio y los convierte en undefined *** REVISAR
      esValorTodasSelect(queryfiltros[f.id_a]) ||
      filtroConfigurado.valor === null
    ) {
      queryfiltros[f.id_a] = undefined;
      if (f.clave_envio) {
        queryfiltros[f.clave_envio.trim()] = undefined;
      }
    }

    if (f.sc_hijos.length > 0) {
      f.sc_hijos.forEach((h) =>
        completarQueryFiltros(queryfiltros, filtrosAAplicar, h)
      );
    }
  };

  filtros.forEach((f) => {
    completarQueryFiltros(queryfiltros, filtrosAAplicar, f);
  });
  return queryfiltros;
};

const resolveActivarFlag = (activarProp, qsBody, modalProps, parametrosURL) => {
  const asBool = (v) =>
    typeof v === "string" ? v.toLowerCase() === "s" : !!v;

  const fromProp = asBool(activarProp);
  const fromQS = asBool(qsBody?.activarListado);
  const fromModal = !!modalProps?.props?.activarListado;
  const fromURL = asBool(parametrosURL?.activarListado);

  return fromProp || fromQS || fromModal || fromURL;
};

const mapDatosSeleccionados = ({
  datos = [],
  opciones = {},
  cabeceras = [],
  incluirClaveEnvio = false,
}) => {
  const seleccionData = opciones?.listado_seleccion_data;

  if (!seleccionData || seleccionData.trim() === "") {
    return datos;
  }

  return datos
    .map((d) => {
      const nd = {};

      seleccionData.split("|").forEach((k) => {
        const cab = cabeceras.find((c) => c.id_a === k);

        if (Object.prototype.hasOwnProperty.call(d, k)) {
          nd[k] = d[k];
          if (incluirClaveEnvio && cab?.clave_envio) {
            nd[cab.clave_envio.trim()] = d[k];
          }
        }
      });

      return nd;
    })
    .filter((d) => Object.keys(d).length > 0);
};

const getDatosPaginaAutoSeleccion = ({
  datos = [],
  opciones = {},
  paginaActual = 1,
}) => {
  if (opciones?.listado_auto_seleccion_por_pagina !== "s") {
    return datos;
  }

  if (opciones?.paginar_backend === "s") {
    return datos;
  }

  const registrosPorPagina =
    Number(opciones?.cantdd_registros) === 0
      ? datos.length
      : Number(opciones?.cantdd_registros) || datos.length;

  const desde = Math.max((paginaActual - 1) * registrosPorPagina, 0);
  const hasta = desde + registrosPorPagina;

  return datos.slice(desde, hasta);
};

export const ListadoProvider = (props) => {
  const {
    configuracion,
    id,
    children,
    modalProps,
    qsBody = {},
    subirDatosSeleccionados,
    padreData, // datos de la vista padre si hubiera
    activarListado, // acepta "s" | true para activar
  } = props;
  const location = useLocation();
  const parametrosURL = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search).entries()),
    [location.search]
  );

  const listadoStateStoreRef = useRef();

  if (!listadoStateStoreRef.current) {
    listadoStateStoreRef.current = createListadoStateStore({
      pantalla: configuracion.opciones.padre,
      subirDatosSeleccionados,
      display_container: configuracion.opciones.display_container,
    });
  }

  const syncListadoState = useCallback((partialState) => {
    listadoStateStoreRef.current.setState(partialState);
  }, []);

  const [loading, setLoading] = useState(false);
  const [carrusel, setCarrusel] = useState(false);

  const [cabeceras, setCabeceras] = useState([]);
  const [filtros, setFiltros] = useState([]);

  const handleSetCabeceras = ({ cabeceras, datos }) => {
    const mapaCab = cabeceras?.filter((c) => c.componente === "mapa").pop();
    if (mapaCab) {
      setCabeceras(() => [mapaCab]);
    }
    if (datos.length > 0) {
      const nc = cabeceras.filter((c) => {
        const key = `${c.id_a}_CONDICION_ACCESO`;

        const existePropiedad = datos.some((d) =>
          Object.prototype.hasOwnProperty.call(d, key)
        );

        if (!existePropiedad) return true;

        return datos.some((d) => d[key] === 1);
      });

      setCabeceras(() => nc);
    }
  };

  const firstRender = useRef(true);
  const yaAutoEjecute = useRef(
    configuracion.opciones.consultaEjecutada === "s"
  ); // evita repetir una consulta ya ejecutada por iniciar_activo en el back

  let pantalla = configuracion.opciones.id_a; //ID_A de Listado
  let id_a = configuracion.opciones.id_a; //ID_A de Listado

  const isAutoSeleccion =
    configuracion.opciones?.listado_seleccion !== "s" &&
    !subirDatosSeleccionados &&
    configuracion.opciones?.listado_seleccion_data &&
    configuracion.opciones?.listado_seleccion_data.trim() !== "" &&
    configuracion.opciones?.listado_auto_seleccion === "s";

  const [display_container, setDisplay] = useState(
    configuracion.opciones.display_container
  );

  const [sideData, setSideData] = useState({});
  const addSideData = useCallback(({ clave_envio, valor }) => {
    setSideData((s) => {
      const newS = { ...s };
      newS[clave_envio] = valor;
      return newS;
    });
  }, []);

  const {
    pantalla: PPantalla,
    configuraciones_ref,
    data: pantallaData,
  } = useContext(PantallaContext);

  const { requestErrorHandler, add_params_to_data } = useContext(FuncionesContext);

  const [state, dispatch] = useReducer(ListadoReducer, initialState);
  const handleCancelar = useCallback(() => {
    dispatch({
      type: "SET_FILTRO_ACTIVO",
      payload: {
        filtros_de_conf: filtros,
        filtros: state.filtrosUsuarioAlCargarPagina,
        funcion: `Boton Cancelar`,
        debug: ["panel de filtros", Date.now()],
      },
    });
    return;
  }, [filtros, state.filtrosUsuarioAlCargarPagina]);

  const actualizarPaginacionStore = useCallback(
    ({ datos = [], opcionesListado = {}, pagina }) => {
      const paginacionBackend = opcionesListado?.paginacion_backend;
      const paginaActual =
        paginacionBackend?.currentPage ?? pagina ?? (datos.length > 0 ? 1 : 0);
      const registrosPorPagina = paginacionBackend?.perPage ?? datos.length;
      const registrosTotales = paginacionBackend?.hasTotal
        ? Number(paginacionBackend.total)
        : datos.length;

      syncListadoState({
        paginaActual: Number(paginaActual) || 0,
        registrosPorPagina: Number(registrosPorPagina) || 0,
        registrosTotales: Number(registrosTotales) || 0,
      });
    },
    [syncListadoState]
  );

  useEffect(() => {
    dispatch({ type: "SET_LOADING_PANTALLA", payload: true });
    syncListadoState({ loading_pantalla: true });
    dispatch({
      type: "SET_ID_GLOBAL",
      payload: id,
    });

    const filtrosUsuario =
      configuracion.opciones.configuracionDeUsuario?.detalles
        .filter((d) => d.conf.tipo.id === 3)
        .map((fu) => {
          return { [fu.conf.id_a]: fu.default };
        })[0];

    dispatch({
      type: "SET_FILTRO_USUARIO_AL_CARGAR_PAGINA",
      payload: filtrosUsuario,
    });
    syncListadoState({
      id_global: id,
      filtrosUsuarioAlCargarPagina: filtrosUsuario,
    });

    if (
      !yaAutoEjecute.current &&
      configuracion.opciones.iniciar_activo === "s" &&
      configuracion.opciones.alerta_max_resultados_aviso === "n"
    ) {
      yaAutoEjecute.current = true;
      callMF({
        filtrosAAplicar: filtrosUsuario,
        _filtros: configuracion.filtros,
        quienLlama: "useEffect Listado id pantalla (iniciar_activo)",
      });
    }

    dispatch({
      type: "SET_LOADING_PANTALLA",
      payload: false,
    });
    setLoading(false);
    syncListadoState({ loading_pantalla: false, loading: false });
  }, [id, pantalla]); // eslint-disable-line react-hooks/exhaustive-deps

  const [data, setData] = useRefDataStore((s) => s.listado);
  const [store, setStore] = useRefStore();

  const callMF = useCallback(
    async ({
      filtrosAAplicar,
      _filtros,
      quienLlama,
      pagina,
      offset,
      reiniciarFiltrosDefaults = false,
    }) => {
      if (!_filtros) _filtros = filtros;
      setLoading(true);
      syncListadoState({ loading: true });

      const parametrosContexto = parametrosURL;

      const filtrosSolicitados = Object.assign(
        {},
        parametrosURL,
        filtrosAAplicar
      );
      let queryfiltros = crearQueryFiltrosParaRequest({
        pantalla,
        reiniciarFiltrosDefaults,
        parametrosContexto,
        crearQuery: () =>
          objetoQueryFiltrosdeF(
            _filtros,
            filtrosSolicitados,
            pantalla,
            "callMF"
          ),
      });
      const filtrosActivos = restaurarValoresTodasEnFiltros({
        filtros: queryfiltros,
        filtrosOriginales: filtrosSolicitados,
        configuracion: _filtros,
      });

      dispatch({
        type: "SET_FILTRO_ACTIVO",
        payload: {
          data,
          filtros_de_conf: configuracion.filtros,
          filtros: filtrosActivos,
          funcion: `callMF funcion ${quienLlama}`,
          debug: [
            "callMF_" + quienLlama,
            filtrosAAplicar,
            _filtros,
            Date.now(),
          ],
        },
      });
      syncListadoState({ filtroActivo: filtrosActivos });
      let newBod = agregarModoFiltrosAlBody(
        Object.assign({}, qsBody, sideData, modalProps?.qsBody, {
          conf_padre: configuracion.opciones.padre,
        }),
        reiniciarFiltrosDefaults,
        parametrosContexto
      );
      newBod.pagina = pagina;
      newBod.offset = offset;

      if (!newBod.id) {
        newBod.id = id; // pasa id al body
      }
      let countsql = null;
      if (
        configuracion.opciones.alerta_max_resultados_aviso === "s" &&
        configuracion.opciones.paginar_backend !== "s"
      ) {
        const countRequest = await axios.post(
          farmageo_api + "/listado/contar/" + PPantalla,
          newBod,
          { params: queryfiltros }
        );
        if (countRequest.data.datos) {
          if (countRequest.data.sql)
            setStore({ sql: addsql(countRequest.data.sql, store.sql) });
          countsql = countRequest.data.sql;
          const ans = await S.fire(countRequest.data.message);
          if (ans.isDenied) {
            setLoading(false);
            syncListadoState({ loading: false });
            return;
          }
        }
      }

      newBod = add_params_to_data(newBod, padreData, configuracion)

      const res = await axios.post(
        farmageo_api + "/config/" + PPantalla,
        newBod,
        { params: queryfiltros }
      );

      if (res.status >= 400) {
        requestErrorHandler(res);
      }
      const datos = res.data?.datos?.meta
        ? res.data.datos.data
        : res.data.datos;

      if (
        res.data.opciones?.listado_seleccion === "s" &&
        subirDatosSeleccionados
      ) {
        subirDatosSeleccionados(datos, res.data.opciones);
      }

      dispatch({
        type: "SOLO_SET_DATOS_SELECCIONADOS",
        payload: [],
      });

      if (isAutoSeleccion) {
        const datosPagina = getDatosPaginaAutoSeleccion({
          datos,
          opciones: res.data.opciones,
          paginaActual: Number(pagina) || 1,
        });
        const dataAutoSeleccionada = mapDatosSeleccionados({
          datos: datosPagina,
          opciones: res.data.opciones,
          cabeceras: res.data.cabeceras ?? configuracion.cabeceras ?? [],
          incluirClaveEnvio: true,
        });

        dispatch({
          type: "SOLO_SET_DATOS_SELECCIONADOS",
          payload: dataAutoSeleccionada,
        });
      }
      try {
        dispatch({
          type: "SET_DATOS",
          payload: datos,
        });
        setData(addData({ datos }));
        syncListadoState({ datos });

        const opcionesListado = Object.assign(res.data?.opciones, modalProps?.props, {
          isAutoSeleccion: isAutoSeleccion,
        });
        dispatch({
          type: "SET_OPCIONES_LISTADO",
          payload: opcionesListado,
        });
        syncListadoState({ opcionesListado });
        actualizarPaginacionStore({
          datos,
          opcionesListado,
          pagina,
        });

        setDisplay(() => res.data.opciones.display_container);
        syncListadoState({
          display_container: res.data.opciones.display_container,
        });

        if (res.data.opciones.listado_boton_update === "s") {
          dispatch({
            type: "SET_BOTONES_LISTADO",
            payload: res.data.listadoBotones,
          });
          syncListadoState({ listadoBotones: res.data.listadoBotones });
          setSideData(() => {
            return {};
          });
          syncListadoState({ sideData: {} });
        }

        if (res.data.sql) {
          const sqls = countsql ? countsql.concat(res.data.sql) : res.data.sql;
          setStore({ sql: addsql(sqls, store.sql) });
        }

        handleSetCabeceras({
          cabeceras: res.data.cabeceras ? res.data.cabeceras : [],
          datos,
        });
        const filtrosRespuesta = res.data.filtros ? res.data.filtros : [];
        setFiltros(filtrosRespuesta);
        syncListadoState({
          cabeceras: res.data.cabeceras ? res.data.cabeceras : [],
          filtros: filtrosRespuesta,
        });

        if (reiniciarFiltrosDefaults) {
          const filtrosDefault = objetoQueryFiltrosdeF(
            filtrosRespuesta,
            {},
            pantalla,
            "defaultsAlRefrescar"
          );
          dispatch({
            type: "SET_FILTRO_ACTIVO",
            payload: {
              data: datos,
              filtros_de_conf: filtrosRespuesta,
              filtros: filtrosDefault,
              funcion: "callMF defaults al refrescar",
              debug: ["defaultsAlRefrescar", filtrosDefault, Date.now()],
            },
          });
          syncListadoState({ filtroActivo: filtrosDefault });
        }

        setLoading(false);
        syncListadoState({ loading: false });

        return res;
      } catch (err) {
        console.log(err);
        setLoading(false);
        syncListadoState({ loading: false });
      }
    },
    [PPantalla, actualizarPaginacionStore, filtros, id, pantalla, parametrosURL, requestErrorHandler, sideData, state.id_global, syncListadoState] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const cambiarPagina = useCallback(
    async (pagina, offset) => {
      syncListadoState({
        paginaActual:
          state.opcionesListado.paginar_backend === "s"
            ? Number(pagina) || 0
            : Number(pagina) + 1 || 0
      });

      if (state.opcionesListado.paginar_backend === "s") {
        if (
          pagina < 0 ||
          state.opcionesListado.paginacion_backend.currentPage === pagina ||
          offset < 1
        )
          throw { error: "Error de pagina/offset" };

        await callMF({
          _filtros: filtros,
          filtrosAAplicar: Object.assign(state.filtroActivo, { id }),
          quienLlama: "useEffect refrescante",
          pagina,
          offset,
        });
      }
    },
    [callMF, filtros, id, state.filtroActivo, state.opcionesListado.paginar_backend, syncListadoState]
  );

  const actualizarListado = useCallback(
    async () =>
      await callMF({
        _filtros: filtros,
        filtrosAAplicar: Object.assign(state.filtroActivo, { id }),
        quienLlama: "useEffect refrescante",
      }),
    [callMF, filtros, id, state.filtroActivo]
  );

  const refrescarEventCallback = useCallback(
    async (event) => {
      if (event.detail === "refrescar") {
        const preparacion = prepararFiltrosParaRefrescar({
          opciones: configuracion.opciones,
          filtroActivo: state.filtroActivo,
          id,
        });
        const res = await callMF({
          _filtros: filtros,
          ...preparacion,
          quienLlama: "useEffect refrescante",
        });

        const eventNew = new CustomEvent("eventNewPantallaData", {
          detail: res.data,
        });
        document.dispatchEvent(eventNew);
      }
    },
    [callMF, configuracion.opciones, filtros, state.filtroActivo, id]
  );

  const actualizarSeleccion = useCallback(
    (event) => {
      if (isAutoSeleccion) {
        const datosPagina = getDatosPaginaAutoSeleccion({
          datos: event.detail.datos,
          opciones: configuracion.opciones,
          paginaActual: 1,
        });
        const dataAutoSeleccionada = mapDatosSeleccionados({
          datos: datosPagina,
          opciones: configuracion.opciones,
          cabeceras: configuracion.cabeceras,
          incluirClaveEnvio: true,
        });

        dispatch({
          type: "SOLO_SET_DATOS_SELECCIONADOS",
          payload: dataAutoSeleccionada,
        });
      }
    },
    [configuracion.cabeceras, configuracion.opciones, isAutoSeleccion]
  );

  useEffect(() => {
    document.addEventListener(id_a, refrescarEventCallback);
    document.addEventListener("eventNewPantallaData", actualizarSeleccion);

    return () => {
      document.removeEventListener(id_a, refrescarEventCallback);
      document.removeEventListener("eventNewPantallaData", actualizarSeleccion);
    };
  }, [actualizarSeleccion, id_a, refrescarEventCallback]);

  // --- Primer render: seteo de datos y filtros base + activación si corresponde ---
  useEffect(() => {
    if (firstRender.current) {
      setLoading(true);
      const datos = configuracion.datos.meta
        ? configuracion.datos.data
        : configuracion.datos;
      dispatch({
        type: "SET_DATOS",
        payload: datos,
      });
      syncListadoState({ datos });

      if (
        subirDatosSeleccionados &&
        configuracion.opciones.listado_seleccion !== "s"
      ) {
        subirDatosSeleccionados(datos, configuracion.opciones);
      }

      if (isAutoSeleccion) {
        const datosPagina = getDatosPaginaAutoSeleccion({
          datos,
          opciones: configuracion.opciones,
          paginaActual: 1,
        });
        const dataAutoSeleccionada = mapDatosSeleccionados({
          datos: datosPagina,
          opciones: configuracion.opciones,
          cabeceras: configuracion.cabeceras,
          incluirClaveEnvio: true,
        });

        dispatch({
          type: "SOLO_SET_DATOS_SELECCIONADOS",
          payload: dataAutoSeleccionada,
        });
        syncListadoState({ datos_seleccionados: dataAutoSeleccionada });
      }

      setData(addData({ datos }));

      handleSetCabeceras({
        cabeceras: configuracion.cabeceras ? configuracion.cabeceras : [],
        datos,
      });
      setFiltros(configuracion.filtros ? configuracion.filtros : []);
      syncListadoState({
        cabeceras: configuracion.cabeceras ? configuracion.cabeceras : [],
        filtros: configuracion.filtros ? configuracion.filtros : [],
      });

      const opcionesListado = Object.assign(configuracion.opciones, modalProps?.props, {
        isAutoSeleccion: isAutoSeleccion,
      });
      dispatch({
        type: "SET_OPCIONES_LISTADO",
        payload: opcionesListado,
      });
      syncListadoState({ opcionesListado });
      actualizarPaginacionStore({ datos, opcionesListado, pagina: 1 });

      dispatch({
        type: "SET_BOTONES_LISTADO",
        payload: configuracion.listadoBotones,
      });
      syncListadoState({ listadoBotones: configuracion.listadoBotones });

      const filtrosIniciales = Object.assign(
        {},
        modalProps?.props?.filtrosDesdeInput,
        qsBody,
        parametrosURL
      );
      const first_filtrosQuery = objetoQueryFiltrosdeF(
        configuracion.filtros,
        filtrosIniciales,
        pantalla,
        "firstRender"
      );
      const first_filtros = restaurarValoresTodasEnFiltros({
        filtros: first_filtrosQuery,
        filtrosOriginales: filtrosIniciales,
        configuracion: configuracion.filtros,
      });

      dispatch({
        type: "SET_FILTRO_ACTIVO",
        payload: {
          filtros_de_conf: configuracion.filtros,
          filtros: first_filtros,
          funcion: `useEffect Listado PPantalla ${PPantalla}`,
          debug: [
            "firstRender",
            objetoQueryFiltrosdeF(
              configuracion.filtros,
              Object.assign(
                {},
                modalProps?.props?.filtrosDesdeInput,
                qsBody,
                parametrosURL
              )
            ),
            configuracion.filtros,
            Date.now(),
          ],
        },
      });
      syncListadoState({ filtroActivo: first_filtros });

      firstRender.current = false;
      setLoading(false);
      syncListadoState({ loading: false });

      // 🔥 NUEVO: activación unificada por prop/qsBody/modal (con guard)
      if (!yaAutoEjecute.current && resolveActivarFlag(activarListado, qsBody, modalProps, parametrosURL)) {
        yaAutoEjecute.current = true;
        callMF({
          _filtros: filtros,
          filtrosAAplicar: first_filtros,
          quienLlama: "firstRender activarListado (prop/qsBody/modal)",
        });
      }
    }
  }, [PPantalla, actualizarPaginacionStore, callMF, configuracion.cabeceras, configuracion.datos, configuracion.filtros, configuracion.listadoBotones, configuracion.opciones, filtros, isAutoSeleccion, modalProps?.props, pantalla, parametrosURL, qsBody, subirDatosSeleccionados, syncListadoState]);

  useEffect(() => {
    syncListadoState({
      loading,
      sideData,
      carrusel,
      display_container,
      datos_seleccionados: state.datos_seleccionados,
      celdas: state.celdas,
      loading_pantalla: state.loading_pantalla,
    });
  }, [
    carrusel,
    display_container,
    loading,
    sideData,
    state.celdas,
    state.datos_seleccionados,
    state.loading_pantalla,
    syncListadoState,
  ]);

  useEffect(() => {
    if (!yaAutoEjecute.current && resolveActivarFlag(activarListado, qsBody, modalProps, parametrosURL)) {
      yaAutoEjecute.current = true;
      const filtrosAAplicar = Object.assign(
        {},
        state.filtroActivo,
        modalProps?.props?.filtrosDesdeInput,
        qsBody,
        parametrosURL
      );
      callMF({
        _filtros: filtros,
        filtrosAAplicar,
        quienLlama: "watch activarListado (prop/qsBody/modal)",
      });
    }
  }, [activarListado, parametrosURL, qsBody?.activarListado, modalProps?.props?.activarListado]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ListadoStateProvider
      store={listadoStateStoreRef.current}
    >
      <ListadoContext.Provider
        value={{
          pantalla: configuracion.opciones.padre,
          datos: state.datos,
          loading_pantalla: state.loading_pantalla,
          loading: loading,
          cabeceras: cabeceras,
          filtroActivo: state.filtroActivo,
          filtrosUsuarioAlCargarPagina: state.filtrosUsuarioAlCargarPagina,
          filtros: filtros,
          opcionesListado: state.opcionesListado,
          opciones: state.opcionesListado,
          listadoBotones: state.listadoBotones,
          datos_seleccionados: state.datos_seleccionados,
          id_global: state.id_global,
          setFilter: callMF, //pasar setQueryFilter para reaccionar a url}
          cambiarPagina,
          actualizarListado: actualizarListado,
          ListadoDispatch: dispatch,
          Dispatch: dispatch,
          dispatch: dispatch,
          addSideData,
          sideData,
          subirDatosSeleccionados,
          celdas: state.celdas,
          carrusel,
          setCarrusel,
          handleCancelar,
        }}
      >
        {/* <button onClick={() => console.log(listadoStateStoreRef.current.getState())}>listadoStore</button> */}
        {display_container === "n" ? (
          <></>
        ) : state.loading_pantalla ? (
          <div style={{ width: "100vw", textAlign: "center" }}>
            <OrixiaSpinner
              text="Cargando Listado ..."
              style={{ width: "100%", textAlign: "center" }}
            />
          </div>
        ) : (
          children
        )}
      </ListadoContext.Provider>
    </ListadoStateProvider>
  );
};

export default ListadoProvider;
